import React, { useEffect } from 'react';
import { Button, Form, Input, Alert, message } from 'antd';
import { changePasswordRequest } from '../../requests/authRequest';

import 'moment/locale/pl';
import 'moment-timezone';

import './ChangePassword.scss';

interface ChangePasswordProps {
  showInfo?: boolean;
}

const ChangePassword = ({ showInfo }: ChangePasswordProps): JSX.Element => {
  const [form] = Form.useForm();

  const onSubmitSuccess = (values: any) => {
    changePasswordRequest({
      newPassword: values.newPassword,
      password: values.password,
      userId: values.userId,
    }).then((data) => {
      if (data && data.code === 2) {
        message.error('Wystąpił błąd, spróbuj ponownie wkrótce.');
      } else if (data && data.code === 3) {
        message.error('Obecne hasło jest błędne.');
      } else if (data && data.code === 4) {
        message.error('Nowe hasło jest błędne.');
      } else if (data && data.code === 5) {
        message.success('Pozytywnie zmieniono hasło.');
        window.location.href = `/`;
        form.resetFields();
      }
    });
  };

  const checkPasswordValidity = (value: string) => {
    const isNonWhiteSpace = /^\S*$/;
    if (!isNonWhiteSpace.test(value)) {
      return Promise.reject(new Error('Hasło nie może zawierać spacji!'));
    }

    const isContainsUppercase = /^(?=.*[A-Z]).*$/;
    if (!isContainsUppercase.test(value)) {
      return Promise.reject(
        new Error('Hasło musi zawierać co najmniej jedną wielką literę!')
      );
    }

    const isContainsLowercase = /^(?=.*[a-z]).*$/;
    if (!isContainsLowercase.test(value)) {
      return Promise.reject(
        new Error('Hasło musi zawierać co najmniej jedną małą literę!')
      );
    }

    const isContainsNumber = /^(?=.*[0-9]).*$/;
    if (!isContainsNumber.test(value)) {
      return Promise.reject(
        new Error('Hasło musi zawierać co najmniej jedną cyfrę!')
      );
    }

    const isContainsSymbol =
      /^(?=.*[~`!@#$%^&*()--+={}\[\]|\\:;"'<>,.?/_₹]).*$/;
    if (!isContainsSymbol.test(value)) {
      return Promise.reject(
        new Error('Hasło musi zawierać co najmniej jeden symbol specjalny.')
      );
    }

    const isValidLength = /^.{12,35}$/;
    if (!isValidLength.test(value)) {
      return Promise.reject(
        new Error('Hasło powinno mieć więcej niż 12 znaków!')
      );
    }

    if (value.replace(/\s/g, '').length < 12) {
      return Promise.reject(
        new Error('Hasło powinno mieć więcej niż 12 znaków!')
      );
    }
    return null;
  };

  const getParamFromUrl = () => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const token = localStorage?.getItem('token');

    // @ts-ignore
    const testToken = JSON.parse(token);
    return urlParams.get('user') || testToken.userId;
  };

  return (
    <>
      <div className="change-password">
        <Form
          name="basic"
          labelCol={{
            span: 8,
          }}
          wrapperCol={{
            span: 16,
          }}
          initialValues={{
            remember: true,
          }}
          form={form}
          onFinish={onSubmitSuccess}
          autoComplete="off"
        >
          {showInfo && (
            <Alert
              message="Co 30 dni ze względów bezpieczeństwa wymagamy aby użytkownicy zmieniali hasło. Ustaw proszę nowe hasło."
              type="info"
              showIcon
            />
          )}
          <Form.Item name="userId" initialValue={getParamFromUrl()}>
            <Input type="hidden" />
          </Form.Item>

          <Form.Item
            label="Obecne hasło"
            name="password"
            rules={[
              {
                required: true,
                message: 'Proszę podać obecne hasło!',
              },
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            label="Nowe hasło"
            name="newPassword"
            rules={[
              {
                required: true,
                message: 'Proszę podać nowe hasło!',
              },
              {
                validator: async (_, value) => {
                  if (value) {
                    if (form.getFieldsValue()?.password === value) {
                      return Promise.reject(
                        new Error('Nowe hasło nie może być takie samo!')
                      );
                    }
                    if (checkPasswordValidity(value)) {
                      return checkPasswordValidity(value);
                    }
                  }
                },
              },
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            name="newPasswordAgain"
            label="Powtórz nowe hasło"
            rules={[
              {
                required: true,
                message: 'Proszę powtórzyć nowe hasło!',
              },
              {
                validator: async (_, value) => {
                  if (value) {
                    if (form.getFieldsValue()?.newPassword !== value) {
                      return Promise.reject(
                        new Error('Nowe hasło się nie zgadza!')
                      );
                    }
                    if (checkPasswordValidity(value)) {
                      return checkPasswordValidity(value);
                    }
                  }
                },
              },
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            wrapperCol={{
              offset: 8,
              span: 16,
            }}
          >
            <Button type="primary" htmlType="submit">
              Zmień hasło
            </Button>
          </Form.Item>
        </Form>
      </div>
    </>
  );
};

export default ChangePassword;
